// src/components/tiptap/ChipView.tsx
// React NodeView for FieldNode. Outer span is contentEditable=false (atomic
// from PM's perspective); inner span is contentEditable=true so the user can
// double-click + edit any word in place. Inner onInput writes through to
// attrs.text — one source of truth.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { getDefaultStore } from "jotai";
import {
  reportFocusAtom,
  dictationGhostChipAtom,
  inlineCardOpenChipIdAtom,
  type DictationGhostChip,
} from "../../atoms/reporterChipAtoms";
import { reviewerVerdictsAtom } from "../../atoms/reviewerPrefsAtoms";
import {
  FINDING_DRAG_MIME,
  resolveSectionKindAt,
  type DraggedFinding,
} from "@/components/tiptap/extensions";
import { CHIP_INNER_CLASS } from "./marks";
import { verdictBadgeText, verdictChipClass } from "./chipVerdict";
import { navigateByLine, moveCaretToPos } from "./verticalNav";

// True when the collapsed caret sits at the very start / very end of the
// inner span's text (multi-text-node safe: measures the text on the far side
// of the caret). Used to decide when Left/Right should step OUT of the chip
// into adjacent prose rather than move within the chip's own text.
//
// The `el.contains(...)` guard is load-bearing: if the window selection is
// somehow outside this chip span, `probe.setEnd/setStart` on a foreign node
// would throw and break the keystroke. Treat "not inside" as "not at the
// boundary" so the arrow falls through to native movement.
function caretAtStart(el: HTMLElement, range: Range): boolean {
  if (!el.contains(range.startContainer)) return false;
  const probe = document.createRange();
  probe.selectNodeContents(el);
  probe.setEnd(range.startContainer, range.startOffset);
  return probe.toString().length === 0;
}
function caretAtEnd(el: HTMLElement, range: Range): boolean {
  if (!el.contains(range.endContainer)) return false;
  const probe = document.createRange();
  probe.selectNodeContents(el);
  probe.setStart(range.endContainer, range.endOffset);
  return probe.toString().length === 0;
}

// Write reportFocusAtom via getDefaultStore() — useSetAtom from this
// component reaches a different Jotai store than PrototypeReport reads,
// because ReactNodeViewRenderer mounts each NodeView as its own React
// root. The default store singleton crosses roots; React Provider context
// does not.

function classNamesFor(attrs: Record<string, unknown>): string {
  const parts = ["report-field"];
  const kind = attrs.kind as string | null;
  const conf = attrs.confidence as string | null;
  const hasLink = attrs.seriesId != null && attrs.sliceIndex != null;
  if (kind) parts.push(`kind-${kind}`);
  if (conf) parts.push(`confidence-${conf}`);
  if (hasLink) parts.push("has-link");
  return parts.join(" ");
}

export const ChipView: React.FC<NodeViewProps> = ({
  node,
  selected,
  editor,
  updateAttributes,
  deleteNode,
  getPos,
}) => {
  const innerRef = useRef<HTMLSpanElement>(null);
  const text = (node.attrs.text as string | null) ?? "";
  // setEditable emits an update without changing this node. Subscribe to the
  // editor so a nested browser editing host cannot remain writable after sign.
  const subscribeEditable = useCallback(
    (notify: () => void) => {
      editor.on("update", notify);
      return () => {
        editor.off("update", notify);
      };
    },
    [editor],
  );
  const isEditable = useSyncExternalStore(subscribeEditable, () => editor.isEditable);

  // In-flight dictation transcript scoped to this chip. Subscribed via
  // the default Jotai store (see file-top comment — NodeView mounts each
  // chip as its own React root, so React Provider context doesn't cross;
  // the singleton default store does).
  const [ghostChip, setGhostChip] = useState<DictationGhostChip | null>(() =>
    getDefaultStore().get(dictationGhostChipAtom),
  );
  useEffect(() => {
    const store = getDefaultStore();
    const unsub = store.sub(dictationGhostChipAtom, () => {
      setGhostChip(store.get(dictationGhostChipAtom));
    });
    return unsub;
  }, []);
  const chipId = (node.attrs.id as string | null) ?? null;
  const condition = (node.attrs.condition as string | null) ?? null;
  // Read the verdict map via the default store (not useAtomValue): the NodeView
  // mounts as its own React root, so if a jotai <Provider> is ever introduced,
  // useAtomValue here and the writer's useSetAtom would resolve to different
  // stores and tints would silently break. The singleton default store crosses
  // roots — consistent with the ghostChip subscription above.
  const [verdicts, setVerdicts] = useState(() => getDefaultStore().get(reviewerVerdictsAtom));
  useEffect(() => {
    const store = getDefaultStore();
    const unsub = store.sub(reviewerVerdictsAtom, () => {
      setVerdicts(store.get(reviewerVerdictsAtom));
    });
    return unsub;
  }, []);
  const verdict = condition ? verdicts[condition]?.verdict : undefined;
  const verdictClass = verdictChipClass(verdict);
  const badge = verdictBadgeText(verdict);
  const ghostText = ghostChip && ghostChip.chipId === chipId ? ghostChip.text : null;
  const ghostOffset = ghostChip && ghostChip.chipId === chipId ? ghostChip.offset : null;

  // Inner-span content management is imperative for caret stability:
  // React reconciliation would replace text nodes on every attrs.text
  // update (every keystroke fires updateAttributes → re-render), and
  // replacing the text node resets the caret to offset 0. With ghost
  // text we splice an extra contentEditable=false span at the captured
  // caret offset — same imperative discipline, two text nodes around
  // the ghost.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    // Capture caret BEFORE mutating, restore AFTER (only for non-ghost
    // updates — the ghost is a transient overlay during dictation, the
    // rad isn't typing).
    const wasFocused = document.activeElement === el && !ghostText;
    let caretOffset: number | null = null;
    if (wasFocused) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (el.contains(range.startContainer)) {
          caretOffset = Math.min(range.startOffset, text.length);
        }
      }
    }
    if (ghostText) {
      const offset = Math.min(ghostOffset ?? text.length, text.length);
      const before = text.slice(0, offset);
      const after = text.slice(offset);
      el.textContent = "";
      if (before) el.appendChild(document.createTextNode(before));
      const ghostSpan = document.createElement("span");
      ghostSpan.className = "dictation-ghost";
      ghostSpan.contentEditable = "false";
      ghostSpan.textContent = ghostText;
      el.appendChild(ghostSpan);
      if (after) el.appendChild(document.createTextNode(after));
    } else if (el.textContent !== text) {
      el.textContent = text;
    }
    if (wasFocused && el.firstChild) {
      const range = document.createRange();
      const offset = caretOffset ?? text.length;
      range.setStart(el.firstChild, Math.min(offset, text.length));
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [text, ghostText, ghostOffset]);

  // Selected styling is applied by ProseMirror via the ProseMirror-selectednode
  // class on the outer node; we mirror it with an extra class for our own CSS.
  useEffect(() => {
    const el = innerRef.current?.parentElement;
    if (!el) return;
    el.classList.toggle("focused", !!selected);
  }, [selected]);

  // Clear the open-card pointer when THIS chip unmounts so a deleted or
  // re-keyed chip never leaves a stale id behind. The InlineChipCard's
  // id-comparison gate makes orphans inert, but explicit cleanup keeps
  // the atom honest under DevTools and future consumers.
  useEffect(() => {
    if (!chipId) return;
    return () => {
      const store = getDefaultStore();
      if (store.get(inlineCardOpenChipIdAtom) === chipId) {
        store.set(inlineCardOpenChipIdAtom, null);
      }
    };
  }, [chipId]);

  // Write the SSoT when the rad clicks into a chip's inner text. This is
  // distinct from Tab navigation (which writes "chip-focused" from
  // marks.ts navigate()) — clicking sets caret-in-chip mode so dictation
  // hard-targets the clicked chip, not whatever the LLM classifier picks.
  //
  // Guard against downgrading a fresh chip-focused write for THIS same
  // chip: Tab nav's rAF inner.focus() (marks.ts navigate, end of dispatch)
  // would otherwise trigger onFocus here and clobber chip-focused with
  // caret-in-chip on every Tab. The visual outline depends on PM-side
  // selected={true}, so chip-focused must survive.
  const handleInnerFocus = () => {
    if (!chipId) return;
    const store = getDefaultStore();
    const current = store.get(reportFocusAtom);
    if (current.mode === "chip-focused" && current.chipId === chipId) return;
    // getPos() is the FieldNode's start position; resolve section against
    // the live doc. NodeView re-renders can stale-close over the editor
    // ref, but `editor.state.doc` is always fresh.
    const pos = typeof getPos === "function" ? getPos() : null;
    const sectionKind = pos != null ? resolveSectionKindAt(editor.state.doc, pos) : null;
    store.set(reportFocusAtom, { mode: "caret-in-chip", chipId, sectionKind });
  };

  const handleInput = (e: React.FormEvent<HTMLSpanElement>) => {
    if (!editor.isEditable) {
      e.currentTarget.textContent = text;
      return;
    }
    const newText = e.currentTarget.textContent ?? "";
    if (newText === text) return;
    // Promote a stock template chip to "rad-added" on first edit so
    // section-delete's classifier (paragraphIsEmptyOrTemplateOnly) treats
    // the rewritten chip as content and triggers the confirm dialog
    // instead of dropping the rad's edit silently.
    const update: { text: string; kind?: string } = { text: newText };
    if (node.attrs.kind === "template") update.kind = "rad-added";
    updateAttributes(update);
    // A transaction filter may reject an input after the browser has already
    // changed this isolated DOM host. Restore its actual document authority.
    const pos = getPos();
    const accepted = typeof pos === "number" ? editor.state.doc.nodeAt(pos)?.attrs.text : text;
    if (typeof accepted === "string" && accepted !== newText)
      e.currentTarget.textContent = accepted;
  };

  const selectAllInner = () => {
    const el = innerRef.current;
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const handleInnerMouseDown = (e: React.MouseEvent<HTMLSpanElement>) => {
    // Double-click keeps the browser's native word-select: the highest-
    // frequency chip edit is swapping one modifier ("moderate" → "mild"),
    // and hijacking detail === 2 into select-whole-chip made that gesture
    // replace the ENTIRE finding with the typed word (pilot, 2026-06-23).
    // The word selection can't escape the chip — the inner span is its own
    // contentEditable root.
    //
    // Triple-click still collapses to "select the whole chip text" for the
    // retype-it-all workflow; natively it would extend selection past the
    // chip into surrounding prose.
    if (e.detail >= 3) {
      e.preventDefault();
      e.stopPropagation();
      // Defer so the browser's selection-update from the earlier clicks
      // doesn't clobber what we set here.
      requestAnimationFrame(selectAllInner);
      return;
    }
    // Single click places the caret for inline text editing — it never
    // opens the attribute card (that's the right-click menu's "Edit
    // attributes" item, or the E shortcut), so a casual click never
    // occludes the findings below. Clicking a chip dismisses a card left
    // open on another chip.
    const store = getDefaultStore();
    if (store.get(inlineCardOpenChipIdAtom) !== null) {
      store.set(inlineCardOpenChipIdAtom, null);
    }
  };

  // GC empty chips on blur: if the rad backspaces all text out of a chip
  // and then clicks/Tabs away, the chip would otherwise persist as an
  // empty pill, taking up a Tab stop and visual space for nothing.
  //
  // Queue the GC for the next focus or selection settlement instead of a
  // hard-coded delay — the previous setTimeout(50) was fragile if future
  // focus-restoration chained setTimeout(0) + microtask + rAF beyond 50ms.
  // We listen for both signals:
  //   - editor.on("selectionUpdate"): Tab → nextField commits NodeSelection
  //     on the target chip, then rAFs focus into its inner span.
  //   - document focusin (capture): mouse click into another chip's inner
  //     span (which lives outside PM, so selectionUpdate doesn't fire) or
  //     into anything else on the page.
  // Whichever fires first wins; we then rAF once so the focus event has
  // a frame to land before checking active-element.
  const handleInnerBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    const el = innerRef.current;
    if (!el || el.textContent !== "") return;
    // If focus moved to another element inside the same chip (jump icon,
    // outer wrapper), don't GC — the rad might still be acting on it.
    const next = e.relatedTarget as Element | null;
    if (next && el.parentElement?.contains(next)) return;
    let done = false;
    const onSettle = () => {
      if (done) return;
      done = true;
      editor.off("selectionUpdate", onSettle);
      document.removeEventListener("focusin", onSettle, true);
      requestAnimationFrame(() => {
        if (!innerRef.current) return;
        if (innerRef.current.textContent !== "") return;
        const active = document.activeElement as Element | null;
        if (active && innerRef.current.parentElement?.contains(active)) return;
        deleteNode();
      });
    };
    editor.on("selectionUpdate", onSettle);
    document.addEventListener("focusin", onSettle, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    // Tab / Shift-Tab must be intercepted HERE — by the time PM's editor-level
    // keymap runs, the browser has already moved focus out of the inner
    // contenteditable (or to the next tabbable widget button). React's
    // synthetic onKeyDown runs first, giving us the chance to preventDefault.
    if (e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) editor.commands.prevField();
      else editor.commands.nextField();
      return;
    }
    // Only plain arrows navigate; Shift (extend selection), Cmd/Ctrl/Alt
    // (word/line/doc jumps) fall through to the browser, matching the
    // editor-level VerticalArrowNav which binds unmodified arrows only.
    const modified = e.shiftKey || e.metaKey || e.ctrlKey || e.altKey;
    // Up/Down inside a chip's inner editing host would otherwise do nothing
    // (or hop to another chip on the same line) — the browser can't move the
    // caret out of this nested contentEditable. Hand off to the shared
    // line-navigation so the caret moves to the adjacent report line and PM
    // regains focus. Editor-level key props don't fire for chip-originated
    // events (ReactNodeView.stopEvent), so this MUST live here. (issue #48)
    if (!modified && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      if (navigateByLine(editor, e.key === "ArrowUp" ? "up" : "down")) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    // Left at the chip's start / Right at its end steps OUT into adjacent
    // prose. A caret mid-word falls through to native movement within the chip.
    if (!modified && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
      const el = innerRef.current;
      const pos = typeof getPos === "function" ? getPos() : null;
      const sel = window.getSelection();
      if (!el || pos == null || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      if (e.key === "ArrowLeft" && caretAtStart(el, range)) {
        e.preventDefault();
        e.stopPropagation();
        moveCaretToPos(editor, pos, -1);
      } else if (e.key === "ArrowRight" && caretAtEnd(el, range)) {
        e.preventDefault();
        e.stopPropagation();
        moveCaretToPos(editor, pos + node.nodeSize, 1);
      }
      return;
    }
    if (e.key === "Backspace") {
      const el = innerRef.current;
      if (!el) return;
      if (el.textContent === "") {
        e.preventDefault();
        deleteNode();
        return;
      }
      const sel = window.getSelection();
      const atStart =
        sel &&
        sel.rangeCount > 0 &&
        sel.getRangeAt(0).collapsed &&
        sel.getRangeAt(0).startOffset === 0;
      if (atStart) {
        // Caret at offset 0 with text still in the chip: nothing before the
        // caret to delete, so swallow the keystroke. Deleting the node here
        // (the old behavior) nuked the whole finding while the rad was
        // editing its first word; letting it bubble would reach FieldNode's
        // editor-level Backspace, which treats chip-adjacent Backspace as
        // chip-select/delete.
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    if (e.key === "Enter") {
      // Enter inside a chip should not produce a newline. Defer to the
      // outer editor's Tab/Next handling instead.
      e.preventDefault();
      return;
    }
  };

  const handleJumpClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const onJump = editor.storage.field?.onJump;
    const seriesId = node.attrs.seriesId as string | null;
    const sliceIndex = node.attrs.sliceIndex as number | null;
    const frameOfReferenceUid = node.attrs.frameOfReferenceUid as string | null;
    if (onJump && seriesId && sliceIndex != null)
      onJump({ seriesId, sliceIndex, frameOfReferenceUid });
  };

  // Click on the inner text places the caret. mousedown on the outer
  // (NodeViewWrapper) should not steal focus back from the inner — the
  // contentEditable=false outer would otherwise prevent the inner caret.
  const handleOuterMouseDown = (e: React.MouseEvent) => {
    if (e.target === innerRef.current) return; // let the inner take the click
  };

  const hasLink = node.attrs.seriesId != null && node.attrs.sliceIndex != null;
  const confidence = node.attrs.confidence as string | null;
  const tierTitle =
    confidence === "confident"
      ? "Included in the draft at this finding's default operating point"
      : confidence === "maybe"
        ? "Review-tier finding: detected one operating-point rung below this finding's default"
        : undefined;
  const pos = typeof getPos === "function" ? getPos() : null;
  const sectionKind = pos == null ? null : resolveSectionKindAt(editor.state.doc, pos);
  const chipKind = node.attrs.kind as string | null;
  const canDragToImpression =
    editor.isEditable &&
    sectionKind !== null &&
    sectionKind !== "impression" &&
    chipKind !== "template" &&
    Boolean(text.trim());

  // NodeViewWrapper owns bubbling dragstart. Capture preserves this payload
  // without replacing Tiptap's handler or changing text-editing gestures.
  const handleDragStart = (event: React.DragEvent) => {
    if (!canDragToImpression || !chipId) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = "copy";
    // PrototypeReport remounts its editor when the study or reader scope changes.
    const finding: DraggedFinding = { editorId: editor.instanceId, findingId: chipId };
    event.dataTransfer.setData(FINDING_DRAG_MIME, JSON.stringify(finding));
    editor.view.dom.classList.add("finding-drag-active");
  };

  const handleDragEnd = () => editor.view.dom.classList.remove("finding-drag-active");

  return (
    <NodeViewWrapper
      as="span"
      className={`${classNamesFor(node.attrs)}${verdictClass ? ` ${verdictClass}` : ""}${canDragToImpression ? " can-drag-to-impression" : ""}`}
      contentEditable={false}
      draggable={canDragToImpression}
      data-testid={canDragToImpression ? "draggable-report-finding" : undefined}
      data-id={node.attrs.id ?? undefined}
      data-condition={node.attrs.condition ?? undefined}
      data-kind={node.attrs.kind ?? undefined}
      data-confidence={node.attrs.confidence ?? undefined}
      title={tierTitle ?? (canDragToImpression ? "Drag finding to Impression" : undefined)}
      data-series-id={node.attrs.seriesId ?? undefined}
      data-for-uid={node.attrs.frameOfReferenceUid ?? undefined}
      data-slice-index={node.attrs.sliceIndex != null ? String(node.attrs.sliceIndex) : undefined}
      // Mirror the `candidates` node attr onto the live DOM for parity with
      // renderHTML's serialized form. The menu / E-M shortcut gates resolve
      // the node by data-id and read attrs directly (chipEditability in
      // PrototypeReport), so this mirror is no longer load-bearing for
      // gating — keep it so live-DOM chips and serialized chips carry the
      // same attribute surface.
      data-candidate-attrs={(node.attrs.candidates as string | null) ?? undefined}
      onMouseDown={handleOuterMouseDown}
      onDragStartCapture={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <span
        ref={innerRef}
        className={CHIP_INNER_CLASS}
        contentEditable={isEditable}
        onBeforeInput={(e) => {
          if (!editor.isEditable) e.preventDefault();
        }}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onMouseDown={handleInnerMouseDown}
        onBlur={handleInnerBlur}
        onFocus={handleInnerFocus}
      />
      {/* text content + in-flight dictation ghost are both set
          imperatively in the useLayoutEffect above — do NOT add a
          {text} child here or the caret resets on every key. */}
      {hasLink ? (
        <button
          type="button"
          className="jump-icon"
          tabIndex={-1}
          title={`Jump to slice ${node.attrs.sliceIndex}`}
          aria-label={`Jump to slice ${node.attrs.sliceIndex}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleJumpClick}
        >
          ↗
        </button>
      ) : null}
      {badge ? (
        <span className="verdict-badge" contentEditable={false} aria-hidden="true">
          {badge}
        </span>
      ) : null}
    </NodeViewWrapper>
  );
};
